import { useCallback, useEffect, useState } from 'react';
import { apiRequest, apiRequestBlob, apiUpload } from '@/lib/api/client';
import { useI18n } from '@/hooks/useI18n';
import { saveDocument } from '../documentDownload';

type Lang = 'kz' | 'ru' | 'en';
type Attachment = { id: string; name: string };
type Draft = {
  group_id: string;
  channels: string[];
  titles: Record<Lang, string>;
  texts: Record<Lang, string>;
  photos: string[];
  documents: string[];
  revision: number;
  request_id: string;
};
type Item = Draft & {
  id: string;
  author_id: string;
  status: string;
  comment: string;
  url?: string;
  attachments: Attachment[];
  can_review: boolean;
  history: { action: string; at: string; comment?: string }[];
};
type Base = {
  user_id: string;
  groups: { id: string; code: string; coordinators: { name: string }[] }[];
  items: Item[];
};
const initial = (): Draft => ({
  group_id: '',
  channels: ['instagram'],
  titles: { kz: '', ru: '', en: '' },
  texts: { kz: '', ru: '', en: '' },
  photos: [],
  documents: [],
  revision: 0,
  request_id: crypto.randomUUID(),
});
export function DoctoralSocialPanel({ onClose }: { onClose: () => void }) {
  const { locale } = useI18n();
  const text = useCallback(
    (ru: string, kz: string, en: string) => (locale === 'kz' ? kz : locale === 'en' ? en : ru),
    [locale],
  );
  const [base, setBase] = useState<Base>();
  const [draft, setDraft] = useState(initial);
  const [editing, setEditing] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void apiRequest<Base>('/doctoral/social', { channel: 'platform' })
      .then((data) => {
        if (!cancelled) setBase(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setBase(undefined);
          setError(e.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);
  const change = (patch: Partial<Draft>) =>
    setDraft((old) => ({ ...old, ...patch, request_id: crypto.randomUUID() }));
  const status = (value: string) =>
    (
      ({
        DRAFT: text('Черновик', 'Жоба', 'Draft'),
        SUBMITTED: text('На проверке', 'Тексерілуде', 'Submitted'),
        RETURNED: text('Возвращено на исправление', 'Түзетуге қайтарылды', 'Returned for changes'),
        FORWARDED: text(
          'Передано на публикацию',
          'Жариялауға жіберілді',
          'Forwarded for publication',
        ),
      }) as Record<string, string>
    )[value] || value;
  const upload = async (file: File, photo: boolean) => {
    setBusy(true);
    setError('');
    try {
      const result = await apiUpload<Attachment>('/doctoral/social-files', file, 'file', {
        photo: String(photo),
      });
      const key = photo ? 'photos' : 'documents';
      change({ [key]: [...draft[key], result.id] });
      setAttachments((old) => [...old, result]);
    } catch (e) {
      setError(e instanceof Error ? e.message : text('Ошибка', 'Қате', 'Error'));
    } finally {
      setBusy(false);
    }
  };
  const save = async (submit: boolean) => {
    setBusy(true);
    setError('');
    try {
      await apiRequest(editing ? `/doctoral/social/${editing}` : '/doctoral/social', {
        channel: 'platform',
        method: editing ? 'PUT' : 'POST',
        body: { ...draft, submit },
      });
      setDraft(initial());
      setEditing('');
      setAttachments([]);
      setTick((t) => t + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : text('Ошибка', 'Қате', 'Error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="p-6 space-y-4">
      <button onClick={onClose}>{text('Назад', 'Артқа', 'Back')}</button>
      <h2>{text('Заявки для соцсетей', 'Әлеуметтік желіге өтінімдер', 'Social media requests')}</h2>
      <button disabled={busy} onClick={() => setTick((t) => t + 1)}>
        {text('Обновить', 'Жаңарту', 'Refresh')}
      </button>
      {error && <p role="alert">{error}</p>}
      {!!base?.groups.length && (
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void save(true);
          }}
        >
          <label>
            {text('Группа', 'Топ', 'Group')}
            <select
              aria-label={text('Группа', 'Топ', 'Group')}
              required
              disabled={busy || !!editing}
              value={draft.group_id}
              onChange={(e) => change({ group_id: e.target.value })}
            >
              <option value="">—</option>
              {base.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.code}
                </option>
              ))}
            </select>
          </label>
          <p>
            {text('Ответственный', 'Жауапты', 'Coordinator')}:{' '}
            {base.groups
              .find((g) => g.id === draft.group_id)
              ?.coordinators.map((u) => u.name)
              .join(', ') || text('Не назначен', 'Тағайындалмаған', 'Not assigned')}
          </p>
          {['instagram', 'website'].map((channel) => (
            <label key={channel}>
              <input
                disabled={busy}
                type="checkbox"
                checked={draft.channels.includes(channel)}
                onChange={(e) =>
                  change({
                    channels: e.target.checked
                      ? [...draft.channels, channel]
                      : draft.channels.filter((c) => c !== channel),
                  })
                }
              />
              {channel === 'instagram' ? 'Instagram' : text('Сайт', 'Сайт', 'Website')}
            </label>
          ))}
          {(['kz', 'ru', 'en'] as Lang[]).map((lang) => (
            <fieldset key={lang}>
              <legend>{lang.toUpperCase()}</legend>
              <label>
                {text('Заголовок', 'Тақырып', 'Title')}
                <input
                  maxLength={250}
                  disabled={busy}
                  required={lang === 'kz' || draft.channels.includes('website')}
                  value={draft.titles[lang]}
                  onChange={(e) => change({ titles: { ...draft.titles, [lang]: e.target.value } })}
                />
              </label>
              <label>
                {text('Текст', 'Мәтін', 'Text')}
                <textarea
                  maxLength={7000}
                  disabled={busy}
                  required={lang === 'kz' || draft.channels.includes('website')}
                  value={draft.texts[lang]}
                  onChange={(e) => change({ texts: { ...draft.texts, [lang]: e.target.value } })}
                />
              </label>
            </fieldset>
          ))}
          <label>
            {text(
              'Фото (до 20 файлов, 10 МБ каждый)',
              'Фото (20 файлға дейін, әрқайсысы 10 МБ)',
              'Photos (up to 20 files, 10 MB each)',
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={busy || draft.photos.length >= 20}
              onChange={(e) => {
                if (e.target.files?.[0]) void upload(e.target.files[0], true);
                e.target.value = '';
              }}
            />
          </label>
          <label>
            {text('Документы', 'Құжаттар', 'Documents')}
            <input
              type="file"
              disabled={busy || draft.documents.length >= 20}
              onChange={(e) => {
                if (e.target.files?.[0]) void upload(e.target.files[0], false);
                e.target.value = '';
              }}
            />
          </label>
          {attachments.map((file) => (
            <p key={file.id}>
              {file.name}
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  change({
                    photos: draft.photos.filter((id) => id !== file.id),
                    documents: draft.documents.filter((id) => id !== file.id),
                  });
                  setAttachments((old) => old.filter((a) => a.id !== file.id));
                }}
              >
                {text('Убрать', 'Алып тастау', 'Remove')}
              </button>
            </p>
          ))}
          <button
            type="button"
            disabled={busy || !draft.group_id || !draft.photos.length}
            onClick={() => void save(false)}
          >
            {text('Сохранить черновик', 'Жобаны сақтау', 'Save draft')}
          </button>
          <button disabled={busy || !draft.photos.length}>
            {text('Отправить ответственному', 'Жауаптыға жіберу', 'Submit to coordinator')}
          </button>
          {editing && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setEditing('');
                setDraft(initial());
                setAttachments([]);
              }}
            >
              {text('Отмена', 'Болдырмау', 'Cancel')}
            </button>
          )}
        </form>
      )}
      {base?.items.map((item) => (
        <article className="border rounded p-4 space-y-2" key={item.id}>
          <h3>{item.titles[locale] || item.titles.kz}</h3>
          <p>
            {status(item.status)} · {item.channels.join(', ')}
          </p>
          <p style={{ whiteSpace: 'pre-wrap' }}>{item.texts[locale] || item.texts.kz}</p>
          <p>{item.comment}</p>
          {item.url && (
            <a href={item.url} target="_blank" rel="noreferrer">
              {text('Ссылка на публикацию', 'Жарияланым сілтемесі', 'Publication link')}
            </a>
          )}
          {item.attachments.map((file) => (
            <button
              key={file.id}
              disabled={busy}
              onClick={async () => {
                try {
                  saveDocument(
                    await apiRequestBlob(`/doctoral/social/${item.id}/files/${file.id}`, {
                      channel: 'platform',
                    }),
                    file.name,
                  );
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'Error');
                }
              }}
            >
              {file.name}
            </button>
          ))}
          {item.author_id === base.user_id && ['DRAFT', 'RETURNED'].includes(item.status) && (
            <button
              disabled={busy}
              onClick={() => {
                setEditing(item.id);
                setDraft({
                  group_id: item.group_id,
                  channels: item.channels,
                  titles: item.titles,
                  texts: item.texts,
                  photos: item.photos,
                  documents: item.documents,
                  revision: item.revision,
                  request_id: crypto.randomUUID(),
                });
                setAttachments(item.attachments);
              }}
            >
              {text('Редактировать', 'Өңдеу', 'Edit')}
            </button>
          )}
          {item.can_review && item.status === 'SUBMITTED' && (
            <div>
              <label>
                {text('Комментарий', 'Пікір', 'Comment')}
                <textarea
                  disabled={busy}
                  maxLength={2000}
                  value={comments[item.id] || ''}
                  onChange={(e) => setComments((old) => ({ ...old, [item.id]: e.target.value }))}
                />
              </label>
              <label>
                {text('Ссылка', 'Сілтеме', 'Link')}
                <input
                  type="url"
                  disabled={busy}
                  maxLength={1000}
                  value={urls[item.id] || ''}
                  onChange={(e) => setUrls((old) => ({ ...old, [item.id]: e.target.value }))}
                />
              </label>
              {['RETURNED', 'FORWARDED'].map((action) => (
                <button
                  key={action}
                  disabled={busy || (action === 'RETURNED' && !comments[item.id]?.trim())}
                  onClick={async () => {
                    setBusy(true);
                    setError('');
                    try {
                      await apiRequest(`/doctoral/social/${item.id}`, {
                        channel: 'platform',
                        method: 'PATCH',
                        body: {
                          revision: item.revision,
                          action,
                          comment: comments[item.id] || '',
                          url: urls[item.id] || '',
                        },
                      });
                      setTick((t) => t + 1);
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Error');
                      setTick((t) => t + 1);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {action === 'RETURNED'
                    ? text('Вернуть на исправление', 'Түзетуге қайтару', 'Return for changes')
                    : text(
                        'Передать на публикацию',
                        'Жариялауға жіберу',
                        'Forward for publication',
                      )}
                </button>
              ))}
            </div>
          )}
          <details>
            <summary>{text('История', 'Тарих', 'History')}</summary>
            {item.history.map((h, i) => (
              <p key={i}>
                {h.at} · {status(h.action)} · {h.comment}
              </p>
            ))}
          </details>
        </article>
      ))}
      {base && !base.items.length && (
        <p>{text('Заявок пока нет', 'Әзірге өтінімдер жоқ', 'No requests yet')}</p>
      )}
    </section>
  );
}
